import { Show } from "../../models/Show";
import { Distribution } from "../../models/Distribution";
import { LotameClient } from "../../../lotame/LotameClient";
import { Logger } from "../../../utils/Logger";
import { addPostalCodeDeclaredBehavior } from "../addPostalCodeDeclaredBehavior";
import { getDataLayerProperties, getOttChannel } from "../../DatalayerUtils";
import { DistributionMode } from "../../constants/DistributionMode";

export const sendMediaMetadata = (
  action: string,
  planName: string,
  discountName: string,
  lotameClient: LotameClient,
): void => {
  Logger.verbose(`LT: sendMediaMetadata invoked with action : ${action}`);
  const dist = dataLayerHelper.get<Distribution>("distribution");
  let behaviors: string[];
  if (dist?.mode === DistributionMode.Live) {
    behaviors = getBehaviorsFromLiveEventMedia(action, planName, discountName);
  } else if (dist?.mode === DistributionMode.Linear) {
    behaviors = getBehaviorsFromDirectLinearMedia(action, planName, discountName);
  } else {
    behaviors = getBehaviorsCatalogMedia(action, planName, discountName);
  }

  addContentType(behaviors);
  concatenateBehaviors(behaviors);

  // Postal Code must be an individual behavior
  addPostalCodeDeclaredBehavior(behaviors);
  lotameClient.callLotameApiCollect({
    behaviors: {
      act: behaviors,
    },
  });
};

function getBehaviorsFromLiveEventMedia(action: string, planName: string, discountName: string): string[] {
  const { dist, page } = getDataLayerProperties();

  const eventName = page?.hierarchy2 ?? "";
  const channel = getOttChannel(dist);

  const behaviors = extractCommonLiveBehaviors(action, planName, discountName, channel, DistributionMode.Live);
  behaviors.push(`Event=${eventName}`);
  behaviors.push(`Mod=${DistributionMode.Live}`);
  return behaviors;
}

function getBehaviorsFromDirectLinearMedia(action: string, planName: string, discountName: string): string[] {
  const { dist } = getDataLayerProperties();
  const station = dist?.channels?.station ?? "";
  const behaviors = extractCommonLiveBehaviors(action, planName, discountName, station, DistributionMode.Linear);
  behaviors.push(`Mod=${DistributionMode.Linear}`);
  return behaviors;
}

function getBehaviorsCatalogMedia(action: string, planName: string, discountName: string): string[] {
  const { show, dist, content, user } = getDataLayerProperties();

  const channel = getOttChannel(dist);
  const subject = show?.subjects?.principal ?? "";
  const genre = show?.genres?.principal ?? "";
  const codename = show?.codeName ?? "";
  const format = show?.format ?? "";
  const season = content?.season ?? "";
  const episode = content?.episode ?? "";
  const connexion = user?.rcid !== "anonyme" ? "login" : "logout";
  const aud = show?.audience?.principal ?? "";

  // The requirement is that we want to send all the following behaviors as independent values plus
  // a single behavior that include all of them.
  // Warning : the order of the items in this array is important!
  return [
    `Ac=${action}`,
    `Ch=${channel}`,
    `SubP=${subject}`,
    `GenP=${genre}`,
    `CE=${codename}`,
    `For=${format}`,
    `Sea=${season}`,
    `Ep=${episode}`,
    `Conn=${connexion}`,
    `Forf=${planName}`,
    `Dis=${discountName}`,
    `Aud=${aud}`,
  ];
}

function concatenateBehaviors(behaviors: string[]): void {
  const concatenatedBehaviors = behaviors.join("|").concat("|");
  behaviors.push(concatenatedBehaviors);
}

function addContentType(behaviors: string[]): void {
  const show = dataLayerHelper.get<Show>("show");
  const contentType = show?.contentType ?? "";
  if (contentType.toLowerCase() === "Contenu publicitaire".toLowerCase()) {
    // We add this behavior only if the content is an ad because we don't want to add new behaviors
    // for regular content (saisons, episodes, etc.)
    behaviors.push(`ConTy=${contentType}`);
  }
}

function extractCommonLiveBehaviors(
  action: string,
  planName: string,
  discountName: string,
  channel: string,
  mode: DistributionMode,
): string[] {
  const { show, content, user } = getDataLayerProperties();
  const subject = content?.subjects?.level2 ?? "";
  const connexion = user?.rcid !== "anonyme" ? "login" : "logout";
  const aud = show?.audience?.principal ?? "";

  const result = [
    `Ac=${action}`,
    `Ch=${channel}`,
    `SubP=${subject}`,
    `Conn=${connexion}`,
    `Forf=${planName}`,
    `Dis=${discountName}`,
  ];
  if (mode === DistributionMode.Linear) {
    result.push(`Aud=${aud}`);
  }
  return result;
}
